import type { App } from './App';
import type { GameConfig, GameMode, GameResult, Participant } from '../types';
import { RoomManager } from '../online/RoomManager';
import { isSupabaseConfigured } from '../online/supabase';
import { parseAndCreateStudents } from '../utils/parser';

type OnlineView = 'choose' | 'creating' | 'waiting-host' | 'joining' | 'waiting-guest' | 'guest-result' | 'error';

interface OnlineModalOptions {
  rosterMode?: boolean;
  onUseNames?: (names: string[]) => void;
  onRoomReady?: (room: RoomManager) => void;
  onClose?: () => void;
  initialCode?: string;
}

export class OnlineModal {
  private el: HTMLElement;
  private app: App;
  private mode: GameMode;
  private view: OnlineView = 'choose';
  private rm: RoomManager | null = null;
  private hostName = '';
  private myGuestName = '';
  private participants: Participant[] = [];
  private errorMsg = '';
  private keepChannelForGame = false;
  private options: OnlineModalOptions;
  private autoCreateStarted = false;
  private guestResult: GameResult | null = null;

  constructor(container: HTMLElement, app: App, mode: GameMode, options: OnlineModalOptions = {}) {
    this.app = app;
    this.mode = mode;
    this.options = options;
    if (options.initialCode) this.view = 'joining';
    else if (options.rosterMode) this.view = 'creating';
    this.el = document.createElement('div');
    this.el.className = 'online-modal-wrap';
    container.appendChild(this.el);
    this.render();
  }

  private render() {
    if (!isSupabaseConfigured()) {
      this.renderError('온라인 기능을 쓰려면 Supabase 환경변수 설정이 필요해요.');
      return;
    }

    switch (this.view) {
      case 'choose': return this.renderChoose();
      case 'creating': return this.renderCreating();
      case 'waiting-host': return this.renderWaitingHost();
      case 'joining': return this.renderJoining();
      case 'waiting-guest': return this.renderWaitingGuest();
      case 'guest-result': return this.renderGuestResult();
      case 'error': return this.renderError(this.errorMsg);
    }
  }

  private renderChoose() {
    const modeLabels: Record<GameMode, string> = {
      single: '한 명 뽑기', multi: '여러 명 뽑기', team: '팀 나누기', reward: '보상 룰렛',
    };
    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          <h3>🌐 온라인 모드</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body choose-body">
          <p class="online-desc">학생들이 자신의 기기로 참여하는 실시간 모드</p>

          <div class="mode-select-row">
            <label class="form-label">게임 모드</label>
            <select class="select-class" id="online-mode">
              ${(['single', 'multi', 'team', 'reward'] as GameMode[]).map((m) =>
                `<option value="${m}" ${m === this.mode ? 'selected' : ''}>${modeLabels[m]}</option>`
              ).join('')}
            </select>
          </div>

          <div class="online-choices">
            <button class="online-choice-card host" id="btn-host">
              <span class="choice-icon">🏫</span>
              <span class="choice-title">방 만들기</span>
              <span class="choice-desc">선생님 - 방을 만들고 학생 초대</span>
            </button>
            <button class="online-choice-card guest" id="btn-join">
              <span class="choice-icon">🎒</span>
              <span class="choice-title">방 참여하기</span>
              <span class="choice-desc">학생 - 선생님이 알려준 코드 입력</span>
            </button>
          </div>
        </div>
      </div>
    `;

    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#online-mode')?.addEventListener('change', (e) => {
      this.mode = (e.target as HTMLSelectElement).value as GameMode;
    });

    this.el.querySelector('#btn-host')?.addEventListener('click', () => {
      this.view = 'creating';
      this.render();
    });
    this.el.querySelector('#btn-join')?.addEventListener('click', () => {
      this.view = 'joining';
      this.render();
    });
  }

  private renderCreating() {
    if (this.options.rosterMode) {
      this.el.innerHTML = `
        <div class="online-modal">
          <div class="modal-header">
            <h3>초대 준비 중</h3>
            <button class="btn-icon-sm" id="btn-close">✕</button>
          </div>
          <div class="modal-body">
            <p class="waiting-msg">학생이 이름만 입력하면 들어올 수 있는 초대 링크를 만들고 있어요.</p>
          </div>
        </div>
      `;
      this.el.querySelector('#btn-close')?.addEventListener('click', () => this.close());
      if (!this.autoCreateStarted) {
        this.autoCreateStarted = true;
        void this.createRoom('선생님');
      }
      return;
    }

    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          <button class="btn-back-sm" id="btn-back">←</button>
          <h3>방 만들기</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <label class="form-label">선생님 이름</label>
          <input class="form-input" id="host-name" type="text" placeholder="예) 김선생님" value="${this.hostName}" />
          <button class="btn-primary" id="btn-create" style="margin-top:16px;width:100%">방 생성</button>
        </div>
      </div>
    `;

    this.el.querySelector('#btn-back')?.addEventListener('click', () => { this.view = 'choose'; this.render(); });
    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.close());

    this.el.querySelector('#btn-create')?.addEventListener('click', async () => {
      const nameEl = this.el.querySelector<HTMLInputElement>('#host-name')!;
      const name = nameEl.value.trim() || '선생님';
      this.hostName = name;
      await this.createRoom(name);
    });
  }

  private async createRoom(hostName: string) {
    this.rm = new RoomManager();
    try {
      const code = await this.rm.createRoom(hostName, this.mode);
      this.view = 'waiting-host';
      this.participants = [{ id: this.rm.getMyId(), name: hostName, isHost: true, joinedAt: Date.now() }];
      this.options.onRoomReady?.(this.rm);
      this.render();

      this.rm.onEvent((ev) => {
        if (ev.type === 'participants') {
          this.participants = ev.participants;
          this.updateParticipantList();
        }
      });
    } catch (e) {
      this.errorMsg = (e as Error).message;
      this.view = 'error';
      this.render();
    }
  }

  private renderWaitingHost() {
    const code = this.rm?.getCode() ?? '---';
    const cleanCode = code.replace('-', '');
    const joinUrl = `${location.origin}${location.pathname}?join=${encodeURIComponent(cleanCode)}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(joinUrl)}`;

    this.el.innerHTML = `
      <div class="online-modal wide">
        <div class="modal-header">
          <h3>학생 대기 중...</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <div class="room-code-display">
            <p class="room-code-label">참가 링크</p>
            <p class="room-code">${code}</p>
            <img class="room-qr" src="${qrUrl}" alt="참가 QR" />
            <p class="room-code-hint">QR이나 링크로 들어오면 이름 입력창만 보여요</p>
            <div class="room-link-row">
              <input class="form-input" id="join-link" value="${joinUrl}" readonly />
              <button class="btn-sm" id="btn-copy-link">링크 복사</button>
            </div>
          </div>

          <div class="participant-section">
            <div class="participant-header">
              <span>참가자 <span id="ptc-count">${this.participants.length}</span>명</span>
            </div>
            <div class="participant-list" id="participant-list">
              ${this.renderParticipantItems()}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" id="btn-cancel-room">방 닫기</button>
          <button class="btn-primary ${this.participants.length < 2 ? 'disabled' : ''}" id="btn-start-online">
            ${this.options.rosterMode ? '명단에 반영' : '🎲 추첨 시작'}
          </button>
        </div>
      </div>
    `;

    this.el.querySelector('#btn-close')?.addEventListener('click', async () => {
      await this.rm?.leave();
      this.close();
    });

    this.el.querySelector('#btn-cancel-room')?.addEventListener('click', async () => {
      await this.rm?.leave();
      if (this.options.rosterMode) {
        this.close();
      } else {
        this.view = 'choose';
        this.render();
      }
    });

    this.el.querySelector('#btn-copy-link')?.addEventListener('click', async () => {
      await navigator.clipboard?.writeText(joinUrl).catch(() => {});
    });

    this.el.querySelector('#btn-start-online')?.addEventListener('click', async () => {
      const guests = this.participants.filter((p) => !p.isHost);
      const names = guests.map((p) => p.name);
      if (this.options.rosterMode) {
        this.options.onUseNames?.(names);
        this.keepChannelForGame = true;
        this.close();
        return;
      }
      await this.rm?.startGame(this.mode, names);
      this.launchGameAsHost(names);
    });
  }

  private renderParticipantItems(): string {
    return this.participants.map((p) => `
      <div class="ptc-item ${p.isHost ? 'host' : ''}">
        <span class="ptc-name">${p.isHost ? '👑 ' : ''}${p.name}</span>
        ${!p.isHost ? `<button class="btn-kick" data-id="${p.id}">강퇴</button>` : ''}
      </div>
    `).join('');
  }

  private updateParticipantList() {
    const list = this.el.querySelector('#participant-list');
    if (list) list.innerHTML = this.renderParticipantItems();
    const cnt = this.el.querySelector('#ptc-count');
    if (cnt) cnt.textContent = String(this.participants.length);
    const startBtn = this.el.querySelector('#btn-start-online');
    if (startBtn) startBtn.classList.toggle('disabled', this.participants.length < 2);

    this.el.querySelectorAll<HTMLButtonElement>('.btn-kick').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await this.rm?.kickParticipant(btn.dataset.id!);
      });
    });
  }

  private renderJoining() {
    const fixedCode = this.options.initialCode ? this.formatCode(this.options.initialCode) : '';
    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          ${fixedCode ? '' : '<button class="btn-back-sm" id="btn-back">←</button>'}
          <h3>${fixedCode ? '이름 입력' : '방 참여하기'}</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          ${fixedCode ? `
            <p class="online-desc">선생님 명단에 표시될 이름을 입력하세요.</p>
            <input id="room-code" type="hidden" value="${fixedCode}" />
          ` : `
            <label class="form-label">접속 코드 (6자리)</label>
            <input class="form-input input-code" id="room-code" type="text"
                   maxlength="7" placeholder="ABC-123" autocapitalize="characters" value="" />
          `}
          <label class="form-label" style="margin-top:12px">이름</label>
          <input class="form-input" id="guest-name" type="text" placeholder="이름을 입력하세요" />
          <button class="btn-primary" id="btn-join-room" style="margin-top:16px;width:100%">명단에 참여</button>
        </div>
      </div>
    `;

    this.el.querySelector('#btn-back')?.addEventListener('click', () => { this.view = 'choose'; this.render(); });
    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.close());

    const codeInput = this.el.querySelector<HTMLInputElement>('#room-code')!;
    if (!fixedCode) {
      codeInput.addEventListener('input', () => {
        codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^(.{3})(.+)/, '$1-$2');
      });
    }

    this.el.querySelector('#btn-join-room')?.addEventListener('click', async () => {
      const code = codeInput.value.replace('-', '');
      const name = (this.el.querySelector<HTMLInputElement>('#guest-name')?.value ?? '').trim();
      if (code.length !== 6 || !name) {
        alert(fixedCode ? '이름을 입력해주세요.' : '코드(6자리)와 이름을 입력해주세요.');
        return;
      }
      this.myGuestName = name;
      await this.joinRoom(code, name);
    });
  }

  private async joinRoom(code: string, name: string) {
    this.rm = new RoomManager();
    try {
      await this.rm.joinRoom(code, name);
      this.view = 'waiting-guest';
      this.render();

      this.rm.onEvent((ev) => {
        if (ev.type === 'participants') {
          this.participants = ev.participants;
          this.updateGuestList();
        } else if (ev.type === 'game_start') {
          this.launchGameAsGuest(ev.names);
        } else if (ev.type === 'game_result' && this.isGameResult(ev.payload)) {
          this.guestResult = ev.payload;
          this.view = 'guest-result';
          this.render();
        } else if (ev.type === 'kicked') {
          alert('선생님이 방에서 내보냈어요.');
          this.close();
        } else if (ev.type === 'host_left') {
          alert('선생님이 방을 닫았어요.');
          this.close();
        }
      });
    } catch (e) {
      this.errorMsg = (e as Error).message;
      this.view = 'error';
      this.render();
    }
  }

  private formatCode(code: string) {
    return code.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^(.{3})(.+)/, '$1-$2').slice(0, 7);
  }

  private renderWaitingGuest() {
    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          <h3>대기 중 ...</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <p class="waiting-msg">명단에 등록했어요. 결과가 발표되면 여기에 내 순위가 나와요.</p>
          <div class="participant-list" id="guest-list">
            ${this.renderParticipantItems()}
          </div>
        </div>
      </div>
    `;
    this.el.querySelector('#btn-close')?.addEventListener('click', async () => {
      await this.rm?.leave();
      this.close();
    });
  }

  private updateGuestList() {
    const list = this.el.querySelector('#guest-list');
    if (list) list.innerHTML = this.renderParticipantItems();
  }

  private renderGuestResult() {
    const result = this.guestResult;
    const message = result ? this.getGuestResultMessage(result) : '결과를 확인하지 못했어요. 잠시 뒤 다시 시도해주세요.';
    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          <h3>결과 안내</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <div class="guest-result-card">
            ${message}
          </div>
        </div>
      </div>
    `;
    this.el.querySelector('#btn-close')?.addEventListener('click', async () => {
      await this.rm?.leave();
      this.close();
    });
  }

  private getGuestResultMessage(result: GameResult): string {
    const myName = this.myGuestName.trim();
    if (result.mode === 'team' && result.teams) {
      const team = result.teams.find((t) => t.members.includes(myName));
      return team
        ? `<p class="guest-result-rank">${team.teamName}</p><p class="guest-result-name">${myName}</p>`
        : `<p class="guest-result-muted">이번 결과에 내 이름이 없어요.</p>`;
    }

    const rank = result.rankings.find((r) => r.name === myName);
    if (rank && this.isAnnouncedRank(result, rank.rank)) {
      return `<p class="guest-result-rank">${rank.rank}위</p><p class="guest-result-name">${rank.name}</p><p class="guest-result-muted">순위권에 들었어요.</p>`;
    }
    if (rank) {
      return `<p class="guest-result-name">${rank.name}</p><p class="guest-result-muted">결과가 발표됐어요. 이번에는 순위권 밖이에요.</p>`;
    }
    return `<p class="guest-result-muted">이번 결과에 내 이름이 없습니다.</p>`;
  }

  private isAnnouncedRank(result: GameResult, rank: number): boolean {
    if (result.mode === 'single') {
      const targetRank = result.config.winnerRule === 'last'
        ? result.rankings.length
        : result.config.winnerRule === 'custom'
          ? Math.max(1, Math.min(result.config.winnerRank ?? 1, result.rankings.length))
          : 1;
      return rank === targetRank;
    }
    return rank <= Math.max(1, result.config.pickCount);
  }

  private isGameResult(value: unknown): value is GameResult {
    return (
      typeof value === 'object' &&
      value !== null &&
      Array.isArray((value as GameResult).rankings) &&
      typeof (value as GameResult).timestamp === 'number'
    );
  }

  private renderError(msg: string) {
    this.el.innerHTML = `
      <div class="online-modal">
        <div class="modal-header">
          <h3>오류</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <p class="error-msg">⚠️ ${msg}</p>
          <button class="btn-secondary" id="btn-retry">다시 시도</button>
        </div>
      </div>
    `;
    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#btn-retry')?.addEventListener('click', () => { this.view = 'choose'; this.render(); });
  }

  private launchGameAsHost(names: string[]) {
      const students = parseAndCreateStudents(names.join('\n'));
    const config: GameConfig = {
      mode: this.mode,
      students,
      pickCount: Math.ceil(students.length * 0.3),
      teamCount: 2,
      teamMode: 'arrival',
      rewards: [],
      mapId: 'pinball-tower',
      shuffleParticipants: true,
      itemsEnabled: false,
      isOnline: true,
      roomCode: this.rm?.getCode(),
      isOnlineHost: true,
      onlineRoom: this.rm ?? undefined,
    };
    this.keepChannelForGame = true;
    this.app.navigate('game', { config });
  }

  private launchGameAsGuest(names: string[]) {
    const students = parseAndCreateStudents(names.join('\n'));
    const config: GameConfig = {
      mode: this.mode,
      students,
      pickCount: Math.ceil(students.length * 0.3),
      teamCount: 2,
      teamMode: 'arrival',
      rewards: [],
      mapId: 'pinball-tower',
      shuffleParticipants: true,
      itemsEnabled: false,
      isOnline: true,
      isOnlineHost: false,
      onlineRoom: this.rm ?? undefined,
    };
    this.keepChannelForGame = true;
    this.app.navigate('game', { config });
  }

  destroy() {
    if (!this.keepChannelForGame) this.rm?.leave();
    this.el.remove();
  }

  private close() {
    if (this.options.onClose) {
      this.options.onClose();
    } else {
      this.app.closeOverlay();
    }
  }
}
